import { AccountTypes } from '@interfaces/models/account-types';
import { IRAOptions, IRATypes } from '@interfaces/models/ira-types';
import { SettlementFundAoAEnum } from '@interfaces/settlement-fund/settlement-fund.enum';
import { createSelector } from '@ngrx/store';
import { DecedentInfoState } from '@store/reducers/decedent-info/decedent-info.reducer';
import { selectDecedentInfoState } from '@store/selectors/decedent-info/decedent-info.selectors';
import { selectInternalTransferFromAccountType } from '@store/selectors/internal-transfer/internal-transfer.selectors';
import { selectSettlementFundChoice } from '@store/selectors/open-account/open-account.selectors';
import {
  selectIraOption,
  selectIraType,
} from '@store/selectors/selected-client-accounts/selected-client-accounts.selectors';
import {
  selectMultiAccountType,
  selectMultiIraOption,
  selectMultiIraType,
} from '@store/selectors/transaction-state/transaction-state.selectors';
import {
  selectAccountType,
  selectIsMultiInheritanceFlow,
  selectIsProspect,
} from '@store/selectors/triage/triage.selectors';
import {
  selectClientPersonalInfoData,
  selectEmploymentFeature,
  selectFinancialsState,
  selectInvestmentObjectivesState,
  selectIsPersonalInfoSSN,
  selectPersonalAddressFeature,
  selectPersonalPhone,
  VgEmploymentState,
  VgPersonalAddressState,
} from 'vg-interaction-model';
import { IFinancialsState } from 'vg-interaction-model/dist/lib/cdd/store/reducers/financials.reducer';
import { IInvestmentObjectivesState } from 'vg-interaction-model/dist/lib/cdd/store/reducers/investment-objectives.reducer';
import { IPersonalInfoClientState } from 'vg-interaction-model/dist/lib/features/personal-info-client/store/reducers/personal-info-client.reducer';
import { IPersonalPhoneState } from 'vg-interaction-model/dist/lib/features/personal-phone/store/reducers/personal-phone.reducer';

import clientDataOAMapper from './oa-helper';

export interface ITransferTypes {
  accountType: AccountTypes;
  iraType?: IRATypes;
  iraOption?: IRAOptions;
}

type PersonalInfoClientState = IPersonalInfoClientState['clientData'] & {
  ssn_private: string;
};

export interface PersonalStates {
  personalInfo: PersonalInfoClientState;
  personalPhone: IPersonalPhoneState;
  decedentInfo?: DecedentInfoState;
}

export const selectOATransactionTypes = createSelector(
  selectAccountType,
  selectIraType,
  selectIraOption,
  (accountType: AccountTypes, iraType: IRATypes, iraOption: IRAOptions): ITransferTypes => {
    return {
      accountType,
      iraType,
      iraOption,
    };
  },
);

export const selectITOATransactionTypes = createSelector(
  selectInternalTransferFromAccountType,
  (accountType: AccountTypes): ITransferTypes => {
    return {
      accountType,
    };
  },
);

export const selectMultiTransactionTypes = createSelector(
  selectMultiAccountType,
  selectMultiIraType,
  selectMultiIraOption,
  (
    accountType: AccountTypes | null,
    iraType: IRATypes | null,
    iraOption: IRAOptions | null,
  ): ITransferTypes => {
    const result: ITransferTypes = {
      accountType: accountType ?? AccountTypes.EMPTY,
    };

    if (iraType) {
      result.iraType = iraType;
    }

    if (iraOption) {
      result.iraOption = iraOption;
    }

    return result;
  },
);

export const selectPersonalStates = createSelector(
  selectClientPersonalInfoData,
  selectIsPersonalInfoSSN,
  selectPersonalPhone,
  selectDecedentInfoState,
  (
    personalInfo: IPersonalInfoClientState['clientData'],
    ssn_private: string,
    personalPhone: IPersonalPhoneState,
    decedentInfo: DecedentInfoState,
  ): PersonalStates => {
    return {
      personalInfo: { ...personalInfo, ssn_private },
      personalPhone: personalPhone,
      decedentInfo: decedentInfo,
    };
  },
);

export const selectITPersonalStates = createSelector(
  selectClientPersonalInfoData,
  selectIsPersonalInfoSSN,
  selectPersonalPhone,
  (
    personalInfo: IPersonalInfoClientState['clientData'],
    ssn_private: string,
    personalPhone: IPersonalPhoneState,
  ): PersonalStates => {
    return {
      personalInfo: { ...personalInfo, ssn_private },
      personalPhone: personalPhone,
    };
  },
);

export const selectOAFromStore = createSelector(
  selectPersonalStates,
  selectPersonalAddressFeature,
  selectEmploymentFeature,
  selectFinancialsState,
  selectInvestmentObjectivesState,
  selectOATransactionTypes,
  selectMultiTransactionTypes,
  selectIsProspect,
  selectSettlementFundChoice,
  selectIsMultiInheritanceFlow,
  (
    personalStates: PersonalStates,
    contactInfo: VgPersonalAddressState,
    employment: VgEmploymentState,
    financial: IFinancialsState,
    invObj: IInvestmentObjectivesState,
    transactionTypes: ITransferTypes,
    multiTransactionTypes: ITransferTypes,
    isProspect: boolean,
    settlementFund: SettlementFundAoAEnum | null,
    isMultiInheritance: boolean,
  ) => {
    const transferToTypes = isMultiInheritance ? multiTransactionTypes : transactionTypes;
    return clientDataOAMapper(
      personalStates,
      contactInfo,
      employment,
      financial,
      invObj,
      transferToTypes,
      settlementFund,
      isProspect,
    );
  },
);

export const selectITOAFromStore = createSelector(
  selectITPersonalStates,
  selectPersonalAddressFeature,
  selectEmploymentFeature,
  selectFinancialsState,
  selectInvestmentObjectivesState,
  selectITOATransactionTypes,
  selectSettlementFundChoice,
  (
    personalStates: PersonalStates,
    contactInfo: VgPersonalAddressState,
    employment: VgEmploymentState,
    financial: IFinancialsState,
    invObj: IInvestmentObjectivesState,
    transactionTypes: ITransferTypes,
    settlementFund: SettlementFundAoAEnum | null,
  ) => {
    return clientDataOAMapper(
      personalStates,
      contactInfo,
      employment,
      financial,
      invObj,
      transactionTypes,
      settlementFund,
    );
  },
);
